import React, { useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "./ui/dialog";

const ROLES = [
  { value: "user", label: "Buyer / Renter" },
  { value: "vendor", label: "Vendor (Selling)" },
  { value: "landlord", label: "Landlord" },
  { value: "tenant", label: "Tenant" },
  { value: "buyer", label: "Buyer (Purchase)" },
];

const routeForRole = (role) => {
  if (role === "admin" || role === "agent") return "/admin";
  if (["vendor", "landlord", "tenant", "buyer"].includes(role)) return "/portal";
  return null; // plain user stays where they are
};

const AuthModal = ({ open, onOpenChange, defaultMode = "login" }) => {
  const { login, register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState(defaultMode);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "user" });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(form.email, form.password);
        toast({ title: `Welcome back, ${u.name.split(" ")[0]}` });
      } else {
        u = await register(form);
        toast({ title: `Welcome to Anadear Estates, ${u.name.split(" ")[0]}` });
      }
      onOpenChange(false);
      setForm({ name: "", email: "", password: "", phone: "", role: "user" });
      const dest = routeForRole(u.role);
      if (dest) navigate(dest);
    } catch (err) {
      toast({ title: "Authentication failed", description: err?.response?.data?.detail || "Please check your details." });
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (email) => setForm({ ...form, email, password: "Password123" });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[440px] p-0 overflow-hidden gap-0">
        <DialogTitle className="sr-only">{mode === "login" ? "Sign in" : "Create your account"}</DialogTitle>
        <div className="bg-[#1c1c1e] px-8 py-7">
          <h2 className="font-display text-white text-[26px] font-300">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-white/60 text-[13px] mt-1">
            {mode === "login" ? "Sign in to your Anadear Estates account" : "Join Anadear Estates to save homes & track progress"}
          </p>
        </div>
        <form onSubmit={submit} className="px-8 py-7 space-y-3">
          {mode === "register" && (
            <>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] bg-white">
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </>
          )}
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
          <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
          <button type="submit" disabled={loading} className="w-full h-[50px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">
            {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
          </button>

          <p className="text-center text-[13px] text-[#6a6a6a] pt-1">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#b8935f] font-500 hover:underline">
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>

          {mode === "login" && (
            <div className="pt-3 border-t border-[#f0ece5]">
              <p className="text-[11px] tracking-[0.1em] uppercase text-[#8a8a8a] mb-2 text-center">Demo accounts (Password123)</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {["landlord@anadear.com", "vendor@anadear.com", "tenant@anadear.com", "buyer@anadear.com"].map((e) => (
                  <button key={e} type="button" onClick={() => fillDemo(e)} className="px-2.5 py-1 rounded-full bg-[#faf7f2] border border-[#e2d8c8] text-[11px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors">
                    {e.split("@")[0]}
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
